import { isNil } from 'lodash';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { Box, IconVariant, ScrollView } from '@fhs-legacy/universal-components';
import { usePathname } from 'hooks/use-pathname';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { useFlag } from 'utils/launchdarkly';
import { getUSState, getUSStateName, useIsMobileOrderingAvailable } from 'utils/restaurant';
import { routes } from 'utils/routing';
import { slugify } from 'utils/slugify';
import {
  webFocusableSectionTextProps,
  webNamedRegion,
  webSectionHeadingWebProps,
} from 'utils/web-named-region';

import { DirectionsLink } from './directions-link';
import { Hours } from './hours';
import StoreMeta from './meta';
import {
  Address,
  Amenity,
  AmenityIcon,
  Background,
  Inner,
  InnerHeader,
  InnerText,
  PhoneNumber,
  SpacingLine,
} from './styled';
import { IModalBody } from './types';

const STORE_INFO_WAYS_TO_ORDER_HEADING_ID = 'store-info-ways-to-order-heading';

const webA11yTransparentWrapperProps =
  Platform.OS === 'web' ? ({ accessible: false } as const) : {};
const webHiddenAddressTextProps =
  Platform.OS === 'web'
    ? ({
        accessibilityElementsHidden: true,
        _web: {
          'aria-hidden': true,
        },
      } as const)
    : {};

export const ModalBody = ({ store }: IModalBody) => {
  const { phoneNumber, restaurantImage, latitude, longitude, physicalAddress } = store;

  const enableShowAddress2BeforeCity = useFlag('enable-show-address2-before-city');
  const enableOrderingFlag = useFlag('enable-ordering');
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;

  const { formatMessage } = useIntl();

  const pathname = usePathname();
  const isStoreLocatorPage = pathname.includes(routes.store);

  const storeSlug = slugify(store.name);
  const streetAddress = physicalAddress?.address1;
  const formattedCity = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince);
  const stateAccessibilityLabel = getUSStateName(
    physicalAddress?.stateProvince || physicalAddress?.stateProvinceShort || state
  );
  const postalCode = physicalAddress?.postalCode?.split('-')[0];
  const address2 = physicalAddress?.address2;
  const showAddress2 = enableShowAddress2BeforeCity && address2;

  const { allServiceModesUnavailable, serviceModeStatus } = useServiceModeStatus(store);
  const shouldShowWaysToOrderSection =
    useIsMobileOrderingAvailable(store) || !allServiceModesUnavailable;
  const hasCatering =
    serviceModeStatus.CATERING_DELIVERY.available || serviceModeStatus.CATERING_PICKUP.available;
  const amenitiesBreakdown: [IconVariant, boolean, string, string][] = [
    ['driveThru', serviceModeStatus.DRIVE_THRU.available, '16px', 'driveThru'],
    ['mobile', useIsMobileOrderingAvailable(store), '22px', 'mobileOrdering'],
    ['delivery', serviceModeStatus.DELIVERY.available, '22px', 'delivery'],
    ['curbside', serviceModeStatus.CURBSIDE.available, '22px', 'curbside'],
    ['catering', hasCatering, '20px', 'catering'],
    ['pickupWindow', serviceModeStatus.PICKUP_WINDOW.available, '24px', 'pickupWindow'],
  ];

  const shouldShowDineIn = serviceModeStatus.EAT_IN.capable && !serviceModeStatus.EAT_IN.disabled;
  const shouldShowTakeout =
    serviceModeStatus.TAKEOUT.capable && !serviceModeStatus.TAKEOUT.disabled;
  const shouldShowDriveThru =
    serviceModeStatus.DRIVE_THRU.capable && !serviceModeStatus.DRIVE_THRU.disabled;
  const shouldShowCurbside =
    serviceModeStatus.CURBSIDE.capable && !serviceModeStatus.CURBSIDE.disabled;
  const shouldShowPickupWindow =
    serviceModeStatus.PICKUP_WINDOW.capable && !serviceModeStatus.PICKUP_WINDOW.disabled;
  const storeHoursBreakdown = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours'],
    [shouldShowCurbside, 'curbsideHours'],
    [shouldShowPickupWindow, 'pickupWindowHours'],
  ];

  const cityStatePostalLine = `${formattedCity}${state ? ` ${state}` : ''}, ${postalCode}`;
  const cityStatePostalAccessibilityLine = `${formattedCity}${
    stateAccessibilityLabel ? ` ${stateAccessibilityLabel}` : ''
  }, ${postalCode}`;
  const addressBodyAccessibilityText = `${
    showAddress2 ? `${address2}, ` : ''
  }${cityStatePostalAccessibilityLine}`;
  const formattedStorePhone = phoneNumber ? formatPhoneForDisplay(phoneNumber) : '';
  const storePhoneAccessibilityLabel = phoneNumber
    ? `${formatMessage({ id: 'phoneNumber' })}, ${formattedStorePhone}`
    : undefined;

  return (
    <>
      {isStoreLocatorPage && (
        <StoreMeta
          atCopy={formatMessage({ id: 'at' })}
          streetAddress={streetAddress}
          city={formattedCity}
          state={state}
          postalCode={postalCode}
          phoneNumber={phoneNumber}
          image={restaurantImage}
          storeSlug={storeSlug}
        />
      )}

      <ScrollView {...webA11yTransparentWrapperProps}>
        <Background {...webA11yTransparentWrapperProps}>
          <Inner {...webA11yTransparentWrapperProps}>
            <Box {...webNamedRegion('modal-heading')}>
              <InnerHeader
                variant="headerTwo"
                testID="address"
                nativeID="modal-heading"
                {...webSectionHeadingWebProps(2)}
              >
                {streetAddress.toLowerCase()}
              </InnerHeader>
              <Box accessible={false}>
                <DirectionsLink latitude={latitude} longitude={longitude} />
                <Box {...webFocusableSectionTextProps(addressBodyAccessibilityText)}>
                  <Address accessible={false} {...webHiddenAddressTextProps}>
                    {showAddress2 && <Address testID="address2">{address2}, </Address>}
                    {cityStatePostalLine}
                  </Address>
                </Box>
                {phoneNumber && (
                  <PhoneNumber
                    testID="phoneNumber"
                    to={`tel:${digitsOnly(phoneNumber)}`}
                    accessibilityLabel={storePhoneAccessibilityLabel}
                  >
                    {formattedStorePhone}
                  </PhoneNumber>
                )}
              </Box>
            </Box>
            <SpacingLine marginTop="$6" bottom="$3" />
            {enableOrdering && shouldShowWaysToOrderSection && (
              <Box {...webNamedRegion(STORE_INFO_WAYS_TO_ORDER_HEADING_ID)}>
                <InnerHeader
                  variant="headerThree"
                  nativeID={STORE_INFO_WAYS_TO_ORDER_HEADING_ID}
                  {...webSectionHeadingWebProps(3)}
                >
                  {formatMessage({ id: 'waysToOrder' })}
                </InnerHeader>
                <Box accessible={false}>
                  {amenitiesBreakdown.map(
                    ([icon, shouldShow, height, messageId]) =>
                      shouldShow && (
                        <Amenity
                          key={messageId}
                          {...webFocusableSectionTextProps(formatMessage({ id: messageId }))}
                        >
                          <AmenityIcon
                            variant={icon}
                            width="40px"
                            height={height}
                            {...hiddenAccessibilityPlatformProps}
                          />
                          <InnerText accessible={false}>
                            {formatMessage({ id: messageId })}
                          </InnerText>
                        </Amenity>
                      )
                  )}
                </Box>
                <SpacingLine marginTop="$1" marginBottom="$4" />
              </Box>
            )}

            {storeHoursBreakdown.map(
              // @ts-expect-error TS(2345) FIXME: Argument of type '([shouldShow, messageId, hours]:... Remove this comment to see the full error message
              ([shouldShow, messageId, hours = messageId]: [boolean, string, string]) =>
                shouldShow && (
                  <Hours
                    key={messageId}
                    title={formatMessage({ id: messageId })}
                    hours={store[hours]}
                    sectionHeadingNativeID={`store-info-hours-${messageId}`}
                  />
                )
            )}
          </Inner>
        </Background>
      </ScrollView>
    </>
  );
};

function digitsOnly(phone: string): string {
  return phone.replace(/\D/g, '');
}

/** US NANP-style grouping for visible text and screen readers. */
function formatPhoneForDisplay(phone: string): string {
  const d = digitsOnly(phone);
  if (d.length === 10) {
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (d.length === 11 && d[0] === '1') {
    const n = d.slice(1);
    return `1-${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`;
  }
  return d.length > 0 ? d : phone.trim();
}
